import {useXRInputSourceStates, useXRInputSourceState, XRControllerState, XRSpace, DefaultXRControllerOptions, useXRInputSourceStateContext, XRControllerModel, DefaultXRInputSourceRayPointerOptions, CombinedPointer, DefaultXRControllerGrabPointer, DefaultXRInputSourceRayPointer, XRSpaceType, useApplyXRSpaceMatrix, useXRSpace} from "@react-three/xr"
import {useFrame, useThree} from "@react-three/fiber";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as THREE from "three";
import {store} from "../../../../Store/index.js";
import {RunInAction} from "mobx-graphlink";
import {observer_mgl_r3} from "../../../../Utils/ThreeJS/General.js";
import {useXREvent_fixed} from "../../../../Utils/ThreeJS/XR/ControllerUtils.js";
import {controllerRight_targetRaySpaceGroup, getControllerPosAndDir} from "../../../../Utils/ThreeJS/XR/Controller.js";
import {Right_InteractionMode, Right_InteractionMode_values} from "../../../../Store/main/tools/vr.js";
import {ZUpGroupHolder} from "../LHQ/ZUpGroup.js";
import {GetControllerSide_Final} from "../../../../Store/main/settings/vr.js";

export const PaintController = observer_mgl_r3(()=>{
	const uiState = store.main.tools.vr;
	const rightController = useXRInputSourceState("controller", GetControllerSide_Final("right"))!;

	useXREvent_fixed("selectstart", useCallback(e=>{
		//console.log("SelectStart:", e);
		if (e.data.handedness == GetControllerSide_Final("right") && uiState.right_interactionMode == "paint") {
			RunInAction("PaintController.selectstart", ()=>uiState.isPainting = true);
		}
	}, []));
	useXREvent_fixed("selectend", useCallback(e=>{
		if (e.data.handedness == GetControllerSide_Final("right") && uiState.isPainting) { //uiState.right_interactionMode == "paint") {
			RunInAction("PaintController.selectend", ()=>uiState.isPainting = false);
		}
	}, []));

	return <Controller controller={rightController} isPainting={uiState.isPainting} /*setIsPainting={setIsPainting}*/ />;
});

const Controller = observer_mgl_r3((props: {controller: XRControllerState, isPainting: boolean /*setIsPainting: (val: boolean)=>void*/})=>{
	const {controller, isPainting} = props;

	// Create a ref for the line
	const lineRef = useRef<THREE.Line|null>(null);

	// Set up the line geometry - pointing forward from the controller
	useEffect(()=>{
		if (lineRef.current) {
			// Create geometry with two points - origin and far away point
			const geometry = new THREE.BufferGeometry().setFromPoints([
				new THREE.Vector3(0, 0, 0),
				new THREE.Vector3(0, 0, -1000) // Line extending 1000 units in negative z-direction
			]);

			lineRef.current.geometry = geometry;
		}
	}, []);

	useFrame(()=>{
		const line = lineRef.current;
		const {pos: controllerPos, dir: controllerDir, dirQuat: controllerDirQuat} = getControllerPosAndDir(controller);
		if (controller && line && controllerPos && controllerDir && controllerDirQuat) {
			// controller pos/dir are in WORLD/root space (y-up), but this component lives inside the ZUpGroup (rotated);
			// convert both into the ZUpGroup's local space before applying (else the ray starts at the wrong point/angle).
			line.position.copy(ZUpGroupHolder.WorldPosToLocal(controllerPos));
			line.quaternion.copy(ZUpGroupHolder.WorldQuatToLocal(controllerDirQuat));
		}
	});

	// alternative Line approach: https://stackoverflow.com/a/71134107
	return (
		<group>
			{/*<primitive object={controller.object}/>*/}
			<Line ref={lineRef}>
				{/*<bufferGeometry />*/}
				<lineBasicMaterial color={isPainting ? "rgb(255,0,0)" : "rgb(255,255,255)"} linewidth={1} transparent={true} opacity={0.5}/>
			</Line>
		</group>
	);
});

const Line = React.forwardRef<THREE.Line, {start?: THREE.Vector3, end?: THREE.Vector3} & React.PropsWithChildren>((props, ref)=>{
	const {children, start = new THREE.Vector3(0, 0, 0), end = new THREE.Vector3(0, 0, -1000)} = props;

	const geometry = useMemo(()=>{
		return new THREE.BufferGeometry().setFromPoints([start, end]);
	}, [start.x, start.y, start.z, end.x, end.y, end.z]);

	useFrame(()=>{
		if (ref && "current" in ref && ref.current && ref.current.geometry !== geometry) {
			ref.current.geometry = geometry;
		}
	});

	return (
		<line ref={ref as any}>
			{children}
		</line>
	);
});